import { logger } from "@/lib/logger";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Json } from "@/integrations/supabase/types";

export interface TrialProtocol {
  id: string;
  trial_id: string;
  protocol_version: string | null;
  inclusion_criteria: Json;
  exclusion_criteria: Json;
  primary_endpoints: Json;
  secondary_endpoints: Json;
  eligibility_requirements: Json;
  dosing_schedule: Json | null;
  adherence_monitoring_enabled: boolean;
  safety_considerations: Json;
  visit_schedule: Json | null;
  created_at: string;
  last_updated: string;
}

export interface ProtocolCriteria {
  items: string[];
}

export interface ProtocolEndpoint {
  name: string;
  description: string;
  timepoint?: string;
}

export function useTrialProtocol(trialId?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['trial-protocol', trialId],
    queryFn: async () => {
      if (!trialId) return null;

      const { data, error } = await supabase
        .from('trial_protocols')
        .select('*')
        .eq('trial_id', trialId)
        .maybeSingle();

      if (error) {
        logger.error('Error fetching trial protocol:', error);
        throw error;
      }

      return data as TrialProtocol | null;
    },
    enabled: !!trialId,
    staleTime: 60 * 1000,
  });

  const upsertMutation = useMutation({
    mutationFn: async (protocol: Partial<Omit<TrialProtocol, 'id' | 'created_at' | 'last_updated'>> & { trial_id: string }) => {
      // Check if protocol exists
      const { data: existing } = await supabase
        .from('trial_protocols')
        .select('id')
        .eq('trial_id', protocol.trial_id)
        .maybeSingle();

      if (existing) {
        // Update
        const { data, error } = await supabase
          .from('trial_protocols')
          .update({
            ...protocol,
            last_updated: new Date().toISOString(),
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert
        const { data, error } = await supabase
          .from('trial_protocols')
          .insert(protocol)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trial-protocol'] });
      toast({
        title: "Protocol saved",
        description: "Trial protocol details updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save protocol",
        variant: "destructive",
      });
    },
  });

  return {
    ...query,
    protocol: query.data,
    isLoading: query.isLoading,
    upsertProtocol: upsertMutation.mutate,
    isSaving: upsertMutation.isPending,
  };
}

// Helper to parse JSON criteria to string array
export function parseCriteriaItems(criteria: Json): string[] {
  if (!criteria) return [];
  if (Array.isArray(criteria)) return criteria.filter(item => typeof item === 'string') as string[];
  if (typeof criteria === 'object' && 'items' in criteria) {
    const items = (criteria as { items?: unknown }).items;
    if (Array.isArray(items)) return items.filter(item => typeof item === 'string') as string[];
  }
  return [];
}

// Helper to parse endpoints
export function parseEndpoints(endpoints: Json): ProtocolEndpoint[] {
  if (!endpoints) return [];
  if (Array.isArray(endpoints)) {
    return endpoints.filter(e => typeof e === 'object' && e !== null).map(e => ({
      name: (e as any).name || '',
      description: (e as any).description || '',
      timepoint: (e as any).timepoint,
    }));
  }
  return [];
}
